/**
 * Carrier MCP — Billing Thresholds (Hex Pattern)
 *
 * Prevents bill shock by:
 *   1. Triggering invoices at predefined spend levels
 *   2. Sending notifications at configurable usage percentages
 *   3. Hard-stopping overages at a user-defined maximum
 *
 * Integrates with Stripe:
 *   - Uses Stripe Billing Thresholds on subscriptions
 *   - Creates threshold-triggered invoices for overage accumulation
 *   - Sends webhook events for notification dispatch
 *
 * Reference: Hex case study — "Mitigating the risk of bill shock"
 *   - Invoices trigger automatically at predefined amounts
 *   - Acts as spend alerts, preventing unexpected charges
 */

import type { Env } from "./types.js";
import type { Tier } from "./billing.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ThresholdConfig {
  /** Soft thresholds — trigger notifications (percentage of limit) */
  notification_thresholds: number[];
  /** Hard threshold — stop overages at this amount (cents). 0 = no hard cap */
  hard_cap_cents: number;
  /** Invoice threshold — trigger invoice at this overage amount (cents) */
  invoice_threshold_cents: number;
  /** Whether to auto-pause overages when hard cap is hit */
  auto_pause_on_cap: boolean;
  /** Email for threshold notifications */
  notification_email: string | null;
  /** Webhook URL for threshold events */
  webhook_url: string | null;
}

export interface ThresholdEvent {
  type: "notification" | "invoice_triggered" | "hard_cap_reached" | "overage_paused";
  sub: string;
  tier: Tier;
  current_spend_cents: number;
  threshold_cents: number;
  pct_of_limit: number;
  timestamp: string;
}

export interface ThresholdCheckResult {
  /** Whether the user can continue consuming overage credits */
  overage_allowed: boolean;
  /** Events triggered by this check */
  events: ThresholdEvent[];
  /** Current spend as percentage of hard cap (0 if no cap) */
  spend_pct: number;
  /** Remaining before hard cap (Infinity if no cap) */
  remaining_cents: number;
}

// ---------------------------------------------------------------------------
// Default Configs
// ---------------------------------------------------------------------------

const DEFAULT_THRESHOLD_CONFIGS: Record<Tier, ThresholdConfig> = {
  free: {
    notification_thresholds: [80, 95, 100],
    hard_cap_cents: 0, // Free tier has no overages
    invoice_threshold_cents: 0,
    auto_pause_on_cap: true,
    notification_email: null,
    webhook_url: null,
  },
  pro: {
    notification_thresholds: [50, 75, 90, 100],
    hard_cap_cents: 50_000, // $500 hard cap default
    invoice_threshold_cents: 10_000, // Invoice every $100
    auto_pause_on_cap: false,
    notification_email: null,
    webhook_url: null,
  },
  enterprise: {
    notification_thresholds: [75, 90],
    hard_cap_cents: 0, // No cap for enterprise
    invoice_threshold_cents: 100_000, // Invoice every $1,000
    auto_pause_on_cap: false,
    notification_email: null,
    webhook_url: null,
  },
  superadmin: {
    notification_thresholds: [],
    hard_cap_cents: 0,
    invoice_threshold_cents: 0,
    auto_pause_on_cap: false,
    notification_email: null,
    webhook_url: null,
  },
};

// ---------------------------------------------------------------------------
// Core Functions
// ---------------------------------------------------------------------------

/**
 * Check billing thresholds before allowing overage consumption.
 * Returns whether overage is still allowed and any triggered events.
 */
export async function checkBillingThresholds(
  env: Env,
  sub: string,
  tier: Tier,
  currentSpendCents: number,
  additionalSpendCents: number,
): Promise<ThresholdCheckResult> {
  const config = await getThresholdConfig(env, sub, tier);
  const events: ThresholdEvent[] = [];
  const projectedSpend = currentSpendCents + additionalSpendCents;

  // Check hard cap
  if (config.hard_cap_cents > 0 && projectedSpend >= config.hard_cap_cents) {
    events.push({
      type: "hard_cap_reached",
      sub,
      tier,
      current_spend_cents: projectedSpend,
      threshold_cents: config.hard_cap_cents,
      pct_of_limit: 100,
      timestamp: new Date().toISOString(),
    });

    if (config.auto_pause_on_cap) {
      events.push({
        type: "overage_paused",
        sub,
        tier,
        current_spend_cents: projectedSpend,
        threshold_cents: config.hard_cap_cents,
        pct_of_limit: 100,
        timestamp: new Date().toISOString(),
      });

      return {
        overage_allowed: false,
        events,
        spend_pct: 100,
        remaining_cents: 0,
      };
    }
  }

  // Check invoice threshold
  if (config.invoice_threshold_cents > 0) {
    const prevInvoiceCount = Math.floor(currentSpendCents / config.invoice_threshold_cents);
    const newInvoiceCount = Math.floor(projectedSpend / config.invoice_threshold_cents);

    if (newInvoiceCount > prevInvoiceCount) {
      events.push({
        type: "invoice_triggered",
        sub,
        tier,
        current_spend_cents: projectedSpend,
        threshold_cents: config.invoice_threshold_cents * newInvoiceCount,
        pct_of_limit: config.hard_cap_cents > 0
          ? Math.round((projectedSpend / config.hard_cap_cents) * 100)
          : 0,
        timestamp: new Date().toISOString(),
      });
    }
  }

  // Check notification thresholds
  if (config.hard_cap_cents > 0) {
    for (const pct of config.notification_thresholds) {
      const thresholdAmount = Math.round((pct / 100) * config.hard_cap_cents);
      if (currentSpendCents < thresholdAmount && projectedSpend >= thresholdAmount) {
        events.push({
          type: "notification",
          sub,
          tier,
          current_spend_cents: projectedSpend,
          threshold_cents: thresholdAmount,
          pct_of_limit: pct,
          timestamp: new Date().toISOString(),
        });
      }
    }
  }

  // Dispatch events (fire-and-forget)
  if (events.length > 0) {
    void dispatchThresholdEvents(env, sub, events, config);
  }

  const spendPct = config.hard_cap_cents > 0
    ? Math.round((projectedSpend / config.hard_cap_cents) * 100)
    : 0;
  const remaining = config.hard_cap_cents > 0
    ? Math.max(0, config.hard_cap_cents - projectedSpend)
    : Infinity;

  return {
    overage_allowed: true,
    events,
    spend_pct: spendPct,
    remaining_cents: remaining,
  };
}

/**
 * Get threshold config for a user, with fallback to tier defaults.
 */
export async function getThresholdConfig(
  env: Env,
  sub: string,
  tier: Tier,
): Promise<ThresholdConfig> {
  const key = `threshold:config:${sub}`;
  const raw = await env.CARRIER_USERS.get(key, "json").catch(() => null);
  if (raw && typeof raw === "object" && "notification_thresholds" in raw) {
    return raw as ThresholdConfig;
  }
  return DEFAULT_THRESHOLD_CONFIGS[tier];
}

/**
 * Update threshold config for a user.
 */
export async function updateThresholdConfig(
  env: Env,
  sub: string,
  updates: Partial<ThresholdConfig>,
): Promise<ThresholdConfig> {
  const tier = await resolveUserTier(env, sub);
  const current = await getThresholdConfig(env, sub, tier);
  const updated: ThresholdConfig = { ...current, ...updates };
  const key = `threshold:config:${sub}`;
  await env.CARRIER_USERS.put(key, JSON.stringify(updated));
  return updated;
}

/**
 * Set up Stripe subscription billing thresholds.
 * Called when a user upgrades to Pro or changes their threshold config.
 */
export async function syncStripeThresholds(
  env: Env,
  sub: string,
  config: ThresholdConfig,
): Promise<boolean> {
  const stripeKey = (env as Env & { STRIPE_SECRET_KEY?: string }).STRIPE_SECRET_KEY;
  if (!stripeKey) return false;

  const subscriptionId = await env.CARRIER_USERS.get(`stripe_subscription_id:${sub}`);
  if (!subscriptionId) return false;

  // Update Stripe subscription with billing thresholds
  const body = new URLSearchParams();

  if (config.invoice_threshold_cents > 0) {
    body.set(
      "billing_thresholds[amount_gte]",
      String(config.invoice_threshold_cents),
    );
  }

  const resp = await fetch(
    `https://api.stripe.com/v1/subscriptions/${subscriptionId}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${stripeKey}`,
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: body.toString(),
    },
  );

  return resp.ok;
}

// ---------------------------------------------------------------------------
// Event Dispatch
// ---------------------------------------------------------------------------

/**
 * Dispatch threshold events to configured notification channels.
 */
async function dispatchThresholdEvents(
  env: Env,
  sub: string,
  events: ThresholdEvent[],
  config: ThresholdConfig,
): Promise<void> {
  // Store events in KV for UI display
  const eventsKey = `threshold:events:${sub}`;
  const existingRaw = await env.CARRIER_USERS.get(eventsKey, "json").catch(() => null);
  const existing = Array.isArray(existingRaw) ? existingRaw as ThresholdEvent[] : [];
  const allEvents = [...existing, ...events].slice(-50); // Keep last 50 events
  await env.CARRIER_USERS.put(eventsKey, JSON.stringify(allEvents), {
    expirationTtl: 35 * 24 * 60 * 60,
  });

  // Webhook dispatch
  if (config.webhook_url) {
    await fetch(config.webhook_url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ events }),
    }).catch(() => {});
  }

  // Audit log (Analytics Engine)
  for (const event of events) {
    const ae = (env as Env & { AUDIT_LOG?: { writeDataPoint: (p: unknown) => void } }).AUDIT_LOG;
    if (ae) {
      ae.writeDataPoint({
        blobs: [
          `threshold_${event.type}`,
          sub,
          event.tier,
        ],
        doubles: [event.current_spend_cents, event.threshold_cents],
        indexes: [sub],
      });
    }
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function resolveUserTier(env: Env, sub: string): Promise<Tier> {
  const raw = await env.CARRIER_USERS.get(`user:${sub}`, "json").catch(() => null);
  if (raw && typeof raw === "object" && "tier" in raw) {
    const t = (raw as { tier: string }).tier;
    if (t === "pro" || t === "enterprise" || t === "superadmin") return t;
  }
  return "free";
}
